/**
 * Small pixel-dialogue-box that flashes above the bird for 1600 ms to
 * confirm a setting change (e.g., "text mode on", "text mode off").
 * Mirrors `CompanionManager.swift:308–317` — same duration, same
 * behavior on toggle.
 *
 * Tracks the bird at 60 fps so the pill stays docked while visible.
 */

const DEFAULT_DISPLAY_DURATION_MS = 1600;
const ERROR_DISPLAY_DURATION_MS = 8000;
const POSITION_TICK_MS = 1000 / 60;
const OFFSET_ABOVE_BIRD_CENTER = 60;

export interface FlashOptions {
  /**
   * Treat as an error: pill turns red, stays visible longer (8 s),
   * and uses a wider variant so multi-word upstream messages aren't
   * truncated. Use this when surfacing the actual reason a turn
   * failed instead of a one-word status.
   */
  variant?: "info" | "error";
  /** Override the auto-hide timer in milliseconds. */
  durationMs?: number;
}

export interface FeedbackPillOptions {
  getBirdCenter: () => { x: number; y: number };
}

export class FeedbackPill {
  private readonly rootElement: HTMLDivElement;
  private readonly textElement: HTMLSpanElement;
  private readonly options: FeedbackPillOptions;
  private hideTimeoutId: number | null = null;
  private positionTimerId: number | null = null;

  constructor(options: FeedbackPillOptions) {
    this.options = options;
    this.rootElement = document.createElement("div");
    this.rootElement.className = "koko-pixel-box koko-feedback-pill";

    const fill = document.createElement("div");
    fill.className = "koko-pixel-box-fill";
    this.textElement = document.createElement("span");
    this.textElement.className = "koko-feedback-pill-text";
    fill.appendChild(this.textElement);
    this.rootElement.appendChild(fill);
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.rootElement);
  }

  flash(text: string, options: FlashOptions = {}): void {
    this.textElement.textContent = text;
    const variant = options.variant ?? "info";
    this.rootElement.classList.toggle("error", variant === "error");
    this.positionToBird();
    this.rootElement.classList.add("visible");
    this.startPositionTimer();

    if (this.hideTimeoutId !== null) {
      window.clearTimeout(this.hideTimeoutId);
    }
    const duration =
      options.durationMs ??
      (variant === "error"
        ? ERROR_DISPLAY_DURATION_MS
        : DEFAULT_DISPLAY_DURATION_MS);
    this.hideTimeoutId = window.setTimeout(() => {
      this.rootElement.classList.remove("visible");
      this.rootElement.classList.remove("error");
      this.stopPositionTimer();
      this.hideTimeoutId = null;
    }, duration);
  }

  destroy(): void {
    if (this.hideTimeoutId !== null) window.clearTimeout(this.hideTimeoutId);
    this.stopPositionTimer();
    this.rootElement.remove();
  }

  private startPositionTimer(): void {
    if (this.positionTimerId !== null) return;
    this.positionTimerId = window.setInterval(
      () => this.positionToBird(),
      POSITION_TICK_MS,
    );
  }

  private stopPositionTimer(): void {
    if (this.positionTimerId !== null) {
      window.clearInterval(this.positionTimerId);
      this.positionTimerId = null;
    }
  }

  private positionToBird(): void {
    const birdCenter = this.options.getBirdCenter();
    const pillWidth = this.rootElement.offsetWidth || 180;
    const pillHeight = this.rootElement.offsetHeight || 40;
    const leftEdge = birdCenter.x - pillWidth / 2;
    const topEdge = birdCenter.y - OFFSET_ABOVE_BIRD_CENTER - pillHeight;
    this.rootElement.style.left = `${Math.max(8, leftEdge)}px`;
    this.rootElement.style.top = `${Math.max(8, topEdge)}px`;
  }
}
